import {
  isCoursesAdminPreviewContext,
  isCoursesAdminPreviewCredential,
  isCoursesAdminPreviewRoute,
} from '@courses/admin-preview-layer/utils/courses-admin-preview-url.utils'
import { hasAdminV2PreviewSession } from '@common/core-layer/utils/admin-preview-token.utils'
import { hasActiveAdminPreviewMarker } from '@common/core-layer/utils/admin-preview-cookie.utils'
import { getHttpStatusFromUnknown } from '@common/core-layer/utils/http-status.utils'
import type { CourseAccessData } from '../services/courses-access-guard.service'

export const PUBLIC_CHECKOUT_ROUTE_PREFIXES = ['/courses/offers/', '/courses/checkout/', '/courses/upsell-offers/one-click/'] as const

export const isPublicCheckoutRoutePath = (path: string): boolean => PUBLIC_CHECKOUT_ROUTE_PREFIXES.some(prefix => path.startsWith(prefix))

export const isCheckoutRouteName = (name: unknown): boolean => name === 'courses-checkout' || name === 'courses-purchase-product'

export const isSentinelProductId = (productId: string | undefined): boolean =>
  productId === 'undefined' || productId === 'null' || productId === '' || productId === '[object Object]'

export const shouldAllowAccessData = (accessData: CourseAccessData | null | undefined): boolean =>
  accessData?.hasAccess === true || accessData?.isAccessExpired === true

export const shouldRedirectNoAccess = (accessData: CourseAccessData | null | undefined): boolean =>
  accessData?.hasAccess === false && accessData?.isAccessExpired !== true

export const shouldAllowRestrictedAccess = (args: { productId: string; libraryProducts: Array<{ id: string }> | undefined }): boolean => {
  return !!args.libraryProducts?.some(product => product.id === args.productId)
}

const isExpiredAccessRecord = (value: unknown): boolean =>
  !!value && typeof value === 'object' && (value as Record<string, unknown>).isAccessExpired === true

/**
 * Only typed expired-access payloads may bypass the guard on error.
 * A bare HTTP 403 is treated as fail-closed unless verified by library ownership.
 */
export const isExplicitExpiredAccessError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }

  const record = error as Record<string, unknown>
  if (isExpiredAccessRecord(record)) {
    return true
  }

  const nested: unknown[] = [record.data, record.payload]
  const response = record.response
  if (response && typeof response === 'object') {
    nested.push((response as Record<string, unknown>).data)
  }

  return nested.some(isExpiredAccessRecord)
}

export const shouldAllowErrorAccess = (args: {
  error: unknown
  productId: string
  libraryProducts: Array<{ id: string }> | undefined
}): boolean => {
  if (isExplicitExpiredAccessError(args.error)) {
    return true
  }
  if (getHttpStatusFromUnknown(args.error) === 403) {
    return shouldAllowRestrictedAccess(args)
  }
  return false
}

export const isCoursesAdminPreviewBypass = (args: {
  path: string
  query: Record<string, unknown>
  isAdminPreview: boolean
  isServer: boolean
  ssrCookieHeader: string | undefined
}): boolean => {
  if (isCoursesAdminPreviewCredential(args.path, args.query)) {
    return true
  }

  if (isCoursesAdminPreviewRoute(args.path) && (args.isAdminPreview || hasAdminV2PreviewSession())) {
    return true
  }

  return args.isServer && (isCoursesAdminPreviewContext(args.path, args.query) || hasActiveAdminPreviewMarker(args.ssrCookieHeader))
}
